import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import { useZinantisMembers } from '../../../../contextsProperty/ContextZinantisMembers';
import { useConnectionRequests } from '../../../../contextsProperty/ContextConnectionRequests';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import IconButton from '../../../../../muiComponents/IconButton';
import Title from '../../../yourProperties/Title';
import SearchBar from '../../../../components/SearchBar';
import useTranslate from '../../../../../customHooks/useTranslate';
import MemberCard from '../../MemberCard';
import RequestConnectionButton from '../../requestButtons/RequestConnectionButton';
import Button from '../../../../../muiComponents/Button';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import routes from '../../../../../utils/commonVariables/routes';
import Icon from '../../../../../muiComponents/Icon';

interface SendRequestPanelProps {
    open: boolean;
    onClose: () => void;
}

const SendRequestPanel = (props: SendRequestPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { members } = useZinantisMembers();
    const [searchTerm, setSearchTerm] = useState('');
    const [idSelectedAgency, setIdSelectedAgency] = useState<string | null>(
        null
    );
    const [isLoading, setIsLoading] = useState(false);
    const { handleSearch } = useConnectionRequests();
    const { handleOwnerZinantisData } = useZinantisMembers();
    const visibleMembers = useMemo(() => {
        return members.filter(
            member =>
                member.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                member.statusKey === 'DISCONNECTED' //solo se pueden enviar solicitudes a miembros desconectados
        );
    }, [members, searchTerm]);

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setIdSelectedAgency(null);
        }
    }, [open]);

    return (
        <LateralPanel
            open={open}
            onClose={onClose}
            className='--xl-width flex-column gap-20'
        >
            <header className='flex flex-between flex-align-center'>
                <Title
                    text='send_new_request'
                    className='text-secondary-color'
                />
                <IconButton iconName='Close' onClick={() => onClose()} />
            </header>
            <Alert
                severity='info'
                sx={{
                    alignItems: 'center',
                    gap: '10px',
                }}
                action={
                    <Link
                        to={
                            routes.landingPrivate +
                            routes.zinantisSection +
                            routes.zinantisMembers
                        }
                        className='white-space-nowrap flex flex-align-center gap-5'
                    >
                        <Icon iconName='CardMembershipOutlined' />
                        {translate('explore_members')}
                    </Link>
                }
            >
                {translate('request_panel_alert')}
            </Alert>
            <SearchBar
                placeholder={translate('search')}
                value={searchTerm}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e?.target.value || '')
                }
                onClear={() => setSearchTerm('')}
            />
            <main className='flex flex-wrap gap-30 height100perc overflow-y-auto'>
                {isLoading ? (
                    <div className='flex-column gap-20 width100perc'>
                        <LoadingPlaceholder width={'100%'} height={'100%'} />
                        <LoadingPlaceholder width={'100%'} height={'100%'} />
                        <LoadingPlaceholder width={'100%'} height={'100%'} />
                    </div>
                ) : (
                    visibleMembers.map(member => {
                        return (
                            <MemberCard
                                key={member.id}
                                member={member}
                                visibleSegmentName
                                visibleNewMember
                                className={`height-fit-content ${
                                    idSelectedAgency === member.id
                                        ? 'connection-requests-panel__member--selected'
                                        : ''
                                }`}
                                onClick={() => setIdSelectedAgency(member.id)}
                            />
                        );
                    })
                )}
            </main>
            <footer className='flex flex-between flex-align-center'>
                <Button
                    variant='outlined'
                    disabled={isLoading}
                    onClick={() => onClose()}
                >
                    {translate('cancel')}
                </Button>
                <RequestConnectionButton
                    idAgency={idSelectedAgency || ''}
                    disabled={!idSelectedAgency || isLoading}
                    onRequestSuccess={async () => {
                        await handleSearch();
                        await handleOwnerZinantisData();
                        onClose();
                    }}
                    onChangeLoading={setIsLoading}
                />
            </footer>
        </LateralPanel>
    );
};

export default SendRequestPanel;
